import { Stack, useMediaQuery, type Theme } from "@mui/material";
import { useMemo, type ReactNode } from "react";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { DataCatalogueEntityType } from "../../api/types/fileTypes";
import type { ObjectClassDefinition, ObjectFieldValuesDetails, PageLayout } from "../../api/types/objectTypes";
import ApprovalWidget from "../approvals/ApprovalWidget";
import WorkflowActionChecks from "../approvals/workflows/WorkflowActionChecks";
import ScrollableColumn from "../common/layout/ScrollableColumn";
import ScrollableColumns from "../common/layout/ScrollableColumns";
import { NotesContextProvider } from "../notes/NotesContextProvider";
import CreatedAndModifiedTimestamps from "./CreatedAndModifiedTimestamps";
import EntityNotesSection from "./EntityNotesSection";
import { getNotesEntityType } from "./entityTypesHelper";
import { collectFieldCitations, type ObjectLayoutSection } from "./helpers";
import ObjectCardChecksContext from "./ObjectCardChecksContext";
import ObjectDuplicatesCheck from "./ObjectDuplicatesCheck";
import { ObjectCardContextProvider } from "./ObjectCardContext";
import ObjectCardDocumentPreviewSection from "./ObjectCardDocumentPreviewSection";
import ObjectEditingLockAlert from "./ObjectEditingLockAlert";
import ObjectFieldsSection from "./ObjectFieldsSection";
import ObjectLogoSection from "./ObjectLogoSection";
import ObjectWorkflowFailureAlert from "./ObjectWorkflowFailureAlert";
import ObjectTasksSection from "./tasks/ObjectTasksSection";
import type { FieldValuesSectionProps } from "./useFieldValuesEditor";

interface Props {
  objectData: ObjectFieldValuesDetails;
  objectDefinition: ObjectClassDefinition;
  cardLayout: PageLayout;
  dataCatalogEntityType: DataCatalogueEntityType;
  hasPermissionsToManageObjectData: boolean;
  hasPermissionsToManageFiles: boolean;
  hasPermissionsToManageNotes: boolean;
  displayedSections: ObjectLayoutSection[];
  renderTopSection?: (objectData: ObjectFieldValuesDetails) => ReactNode;
  renderBanner?: (objectData: ObjectFieldValuesDetails, cardLayout: PageLayout) => ReactNode;
  renderFieldValues?: (props: FieldValuesSectionProps) => ReactNode;
  onEntityUpdate: (newObjectData: ObjectFieldValuesDetails) => void;
  onApprovalActionExecuted: () => void;
  onDocumentDeleted: () => void;
  onUnsavedEditsChange: (hasUnsavedEdits: boolean) => void;
}

const ObjectMainInfo = ({
  objectData,
  objectDefinition,
  cardLayout,
  dataCatalogEntityType,
  hasPermissionsToManageObjectData,
  hasPermissionsToManageFiles,
  hasPermissionsToManageNotes,
  displayedSections,
  renderTopSection,
  renderBanner,
  renderFieldValues,
  onEntityUpdate,
  onApprovalActionExecuted,
  onDocumentDeleted,
  onUnsavedEditsChange,
}: Props) => {
  const { actionChecks, duplicates } = useDefinedContext(ObjectCardChecksContext, "ObjectCardChecksContext");

  const isLargerScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));

  const fieldCitations = useMemo(() => collectFieldCitations(objectData.fieldsCitations), [objectData.fieldsCitations]);

  const actionsWithChecks = objectDefinition.workflowActionsWithChecks ?? [];

  const objectLocksForUser = objectData.locks.filter((lock) => !lock.canUserBypassLock);

  const allowEditingObjectData =
    objectData.locks.length === 0 ? hasPermissionsToManageObjectData : objectLocksForUser.length === 0;

  const isWorkflowFailed = objectData.workflowMetadata.some((m) => Boolean(m.failureMessage));

  const isRightSectionCollapsible = displayedSections.includes("notes") && displayedSections.includes("file_preview");

  return (
    <ObjectCardContextProvider fieldCitations={fieldCitations} onUnsavedEditsChange={onUnsavedEditsChange}>
      <ScrollableColumns gridProps={{ columnSpacing: 2.5 }}>
        <ScrollableColumn>
          <Stack spacing={2.5} sx={{ width: "100%" }}>
            {duplicates && (
              <ObjectDuplicatesCheck objectType={objectDefinition.objectType} objectDuplicates={duplicates} />
            )}

            {actionChecks && (
              <WorkflowActionChecks
                action={actionChecks.action}
                blockers={actionChecks.blockers}
                warnings={[]}
                hasFailed={
                  actionChecks.isOfferedByWorkflow && hasPermissionsToManageObjectData && actionChecks.hasFailed
                }
              />
            )}

            {renderTopSection?.(objectData)}

            {(objectData.approvalMetadata ?? []).map((approval) => (
              <ApprovalWidget
                key={approval.approvalId}
                approvalId={approval.approvalId}
                showActionChecks={actionsWithChecks.includes("Approve")}
                onApprovalActionExecuted={onApprovalActionExecuted}
              />
            ))}

            {objectLocksForUser.length > 0 && <ObjectEditingLockAlert objectLocks={objectLocksForUser} />}

            {isWorkflowFailed && <ObjectWorkflowFailureAlert workflowMetadata={objectData.workflowMetadata} />}

            {renderBanner?.(objectData, cardLayout)}

            <ObjectFieldsSection
              object={objectData}
              objectDefinition={objectDefinition}
              cardLayout={cardLayout}
              allowEditing={allowEditingObjectData}
              onEntityUpdate={onEntityUpdate}
              renderFieldValues={renderFieldValues}
            />

            {displayedSections.includes("tasks") && (
              <ObjectTasksSection
                objectData={objectData}
                hasPermissionsToManageObjectData={hasPermissionsToManageObjectData}
                onEntityUpdate={onEntityUpdate}
                onApprovalActionExecuted={onApprovalActionExecuted}
              />
            )}

            {displayedSections.includes("logo") && (
              <ObjectLogoSection
                entity={objectData}
                disabled={!allowEditingObjectData}
                onEntityUpdate={onEntityUpdate}
              />
            )}

            <CreatedAndModifiedTimestamps
              createdAt={objectData.createdAt}
              modifiedAt={objectData.updateAt}
              stackProps={{ sx: { pb: 1.5 } }}
            />
          </Stack>
        </ScrollableColumn>

        <ScrollableColumn width={isLargerScreen && displayedSections.includes("file_preview") ? "52rem" : "36rem"}>
          <Stack spacing={2.5} sx={{ width: "100%", height: "100%", pb: 1 }}>
            {displayedSections.includes("file_preview") && (
              <ObjectCardDocumentPreviewSection
                dataCatalogEntityType={dataCatalogEntityType}
                objectType={objectData.objectType}
                objectId={objectData.id}
                hasPermissionsToManageFiles={hasPermissionsToManageFiles && allowEditingObjectData}
                collapsible={isRightSectionCollapsible}
                onDocumentDeleted={onDocumentDeleted}
              />
            )}

            {displayedSections.includes("notes") && (
              <NotesContextProvider
                entityType={getNotesEntityType(objectDefinition.objectType)}
                entityId={objectData.id}
                objectType={objectData.objectType}
                hasPermissionsToManageNotes={hasPermissionsToManageNotes}
              >
                <EntityNotesSection collapsible={isRightSectionCollapsible} />
              </NotesContextProvider>
            )}
          </Stack>
        </ScrollableColumn>
      </ScrollableColumns>
    </ObjectCardContextProvider>
  );
};

export default ObjectMainInfo;
